// ---------------------------------------------------------------------------
// ManagementViewPage (PRODOPSAI-387)
//
// Phase-0 page shell for the new Management View. Renders the page chrome
// (breadcrumbs, header, FilterBar, tab list) and **role-gated** tabs whose
// visibility is driven by the response from `GET /api/management-view/config`.
//
// Key contract decisions (per US1 spec + epic):
//
//  - The route `/management-view` is OPEN to every authenticated user — no
//    `RoleBasedRoute` wrapper. Role filtering happens here, in the page,
//    based on the config endpoint's `visibleTabs` array. The endpoint is the
//    single source of truth for which tabs a role can see, so flipping a
//    role↔tab DB row enables a tab without a code deploy (per AC2.4).
//
//  - The tab list is a 1:1 reflection of `visibleTabs`. Tabs missing from
//    the array are NOT rendered at all (no greyed state, no lock icon).
//
//  - `?tab=` is read once on mount via `useSearchParams` (matching
//    `ManagementDashboardPage:31-54`). User tab clicks update local state
//    only — there is no URL write-back. Locked-tab fallback is silent (no
//    banner, no toast) per AC2.2.
//
//  - All five Phase-0 tabs render an `<EmptyTabPlaceholder />`. Real content
//    lands in Phases 1–3.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ErrorBanner,
  PageHeader,
  Spinner,
  TabContent,
  TabList,
  TabTrigger,
  Tabs,
} from '@/components/ui';
import { PageContainer } from '@/components/layout';
import { FilterBar } from '@/components/management-view/FilterBar';
import { EmptyTabPlaceholder } from '@/components/management-view/EmptyTabPlaceholder';
import { OverviewTab } from '@/components/management-view/OverviewTab';
import { QualityControlTab } from '@/components/management-view/QualityControlTab';
import { useManagementViewConfig } from '@/hooks/use-management-view-config';
import { useManagementViewFilters } from '@/hooks/use-management-view-filters';
import type { TabKey } from '@skystone/shared';

const VALID_TAB_KEYS: ReadonlySet<TabKey> = new Set<TabKey>([
  'overview',
  'quality_control',
  'product_flow',
  'dora',
  'code_quality',
]);

/** Display labels for each tab (Phase 0 — no count badges yet). */
const TAB_LABEL: Record<TabKey, string> = {
  overview: 'Overview',
  quality_control: 'Quality Control',
  product_flow: 'Product Flow',
  dora: 'DORA',
  code_quality: 'Code Quality',
};

/**
 * Empty placeholder content for each tab. Centralised here (not inside the
 * render function) per SonarCloud rule S7721 — helper functions defined
 * inside a component render trip the rule.
 */
function placeholderForTab(tab: TabKey): JSX.Element {
  switch (tab) {
    case 'overview':
      // Phase 2 (PRODOPSAI-404): real Overview tab replaces the Phase-0
      // placeholder. Renders HighlightsCard (filter-driven) + MaturityCard
      // (self-contained) in a two-column responsive layout.
      return <OverviewTab />;
    case 'quality_control':
      // Phase 1 (PRODOPSAI-398): real Quality Control tab replaces the
      // Phase-0 placeholder. Renders three matrices (Status Count,
      // Issue-Type Compliance, Epic Linkage) via the bulk endpoint.
      return <QualityControlTab />;
    case 'product_flow':
      return (
        <EmptyTabPlaceholder
          phase={3}
          message="Coming in Phase 3 — Product Flow metrics"
        />
      );
    case 'dora':
      return (
        <EmptyTabPlaceholder
          phase={3}
          message="Coming in Phase 3 — DORA metrics"
        />
      );
    case 'code_quality':
      return (
        <EmptyTabPlaceholder
          phase={3}
          message="Coming in Phase 3 — Code Quality metrics"
        />
      );
    default:
      // exhaustiveness guard — TabKey union covers all five branches above
      return <EmptyTabPlaceholder phase={0} message="Unknown tab" />;
  }
}

/**
 * Resolve the initial active tab from the URL.
 *
 * Returns the requested tab when:
 *   - `?tab=` is a known TabKey, AND
 *   - it is present in the user's `visibleTabs`.
 *
 * Otherwise falls back to the first visible tab (typically `overview`). The
 * fallback is silent (no banner, no toast) per AC2.2 — the URL itself stays
 * untouched.
 */
function resolveInitialTab(rawParam: string | null, visibleTabs: TabKey[]): TabKey {
  const fallback = visibleTabs[0] ?? 'overview';
  if (rawParam === null) return fallback;

  const isKnownTabKey = VALID_TAB_KEYS.has(rawParam as TabKey);
  if (isKnownTabKey && visibleTabs.includes(rawParam as TabKey)) {
    return rawParam as TabKey;
  }
  return fallback;
}

/** Stand-in breadcrumb row matching the mockup. Not a routing primitive. */
function Breadcrumbs() {
  return (
    <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
      <span>Skystone</span>
      <span aria-hidden="true" className="px-1">›</span>
      <span className="text-foreground">Organisational View</span>
    </nav>
  );
}

export function ManagementViewPage() {
  const { data: config, loading, error } = useManagementViewConfig();
  const filters = useManagementViewFilters();
  const [searchParams] = useSearchParams();

  // `null` until the config has resolved AND we've picked a starting tab.
  // Initialised inside an effect so we can read `visibleTabs` from the
  // resolved config; the URL `?tab=` lookup happens once at that point.
  const [activeTab, setActiveTab] = useState<TabKey | null>(null);

  useEffect(() => {
    if (config && activeTab === null) {
      const initialParam = searchParams.get('tab');
      setActiveTab(resolveInitialTab(initialParam, config.visibleTabs));
    }
    // We only honour the URL once on first config arrival; subsequent
    // searchParams changes (e.g. filter writes) must not stomp the user's
    // manually-clicked tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  return (
    <PageContainer variant="wide" className="space-y-6 p-6">
      <Breadcrumbs />

      <PageHeader
        title="Organisational View"
        subtitle="Trajectory across all teams. One matrix per metric, color-coded by threshold."
      />

      <FilterBar
        scope={filters.scope}
        window={filters.window}
        cadence={filters.cadence}
        includeArchived={filters.includeArchived}
        onWindowChange={filters.setWindow}
        onCadenceChange={filters.setCadence}
        onIncludeArchivedChange={filters.setIncludeArchived}
      />

      <ErrorBanner message={error} />

      {renderTabsArea({
        loading,
        error,
        config,
        activeTab,
        onTabChange: setActiveTab,
      })}
    </PageContainer>
  );
}

interface TabsAreaProps {
  loading: boolean;
  error: string | null;
  config: { visibleTabs: TabKey[] } | null;
  activeTab: TabKey | null;
  onTabChange: (next: TabKey) => void;
}

/**
 * Render the loading spinner, the safe-fallback Overview placeholder, or the
 * full role-gated tab list based on the config-fetch state. Lifted out of
 * `ManagementViewPage` to keep the component flat (S3776).
 */
function renderTabsArea({
  loading, error, config, activeTab, onTabChange,
}: Readonly<TabsAreaProps>): JSX.Element {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="management-view-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  // Safe fallback when the config endpoint is unreachable: render the
  // Overview placeholder so the page is never blank, but skip the tab list.
  if (error || !config) {
    return (
      <div data-testid="management-view-error-fallback">
        {placeholderForTab('overview')}
      </div>
    );
  }

  const visibleTabs = config.visibleTabs;
  const current = activeTab ?? visibleTabs[0] ?? 'overview';

  return (
    <Tabs value={current} onValueChange={(v) => onTabChange(v)}>
      <TabList>
        {visibleTabs.map((tab) => (
          <TabTrigger key={tab} value={tab}>
            {TAB_LABEL[tab]}
          </TabTrigger>
        ))}
      </TabList>
      {visibleTabs.map((tab) => (
        <TabContent key={tab} value={tab}>
          {placeholderForTab(tab)}
        </TabContent>
      ))}
    </Tabs>
  );
}
